// "Usage and Credits" — Figma nodes 700:728 (no credits), 700:953 (add
// credits), 701:2810 (payment successful) and 700:2030 (view credit usage).
//
// One component, four views driven by data rather than four screens:
//   credits  — balance, Add credits / View usage, auto-refill, receipts
//   add      — the pack catalog + Pay now
//   usage    — real charge/refund history from GET /sparks/transactions
//   success  — the post-checkout confirmation
//
// Available credits is the REAL balance from GET /sparks and never anything
// else. A sandbox purchase does not move it, so the success copy is worded
// against the ORDER ("2,000 credits purchased") rather than claiming a new
// balance — the one place this deliberately departs from the Figma copy,
// because the alternative is a UI that lies about the user's money.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Check, Download, CircleCheck } from 'lucide-react'

import { useAuth } from '../../../../contexts/AuthContext'
import { useSparks, useBillingCatalog } from '../../../../api/hooks'
import { api, ApiRequestError } from '../../../../api/client'
import { isApiRecord } from '../../../../api/responseDecoders'
import { useBooleanFlagValue } from '../../../../hooks/useBooleanFlagValue'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'
import SandboxNotice from '../../../../features/billing/SandboxNotice'
import {
  formatCredits,
  formatMoney,
  type CreditPack,
  type SandboxOrder,
} from '../../../../features/billing/billingTypes'
import {
  listOrders,
  saveOrder,
  subscribeToOrders,
} from '../../../../features/billing/sandboxOrderStore'
import { SAMPLE_RECEIPT_URL } from '../../../../features/billing/receipt'
import { useSettingsModal } from '../SettingsModalProvider'
import {
  trackCheckoutInitiateClick,
  trackTokenCatalogOptionClick,
  trackTokenCatalogView,
  trackUsageAndTokensView,
} from '../../../../analytics/events'
import { useActiveTeam } from '../../../../contexts/ActiveTeamContext'
import { teamContextFor } from '../../../../analytics/teamContext'

const log = createNadDebug('dw:billing-sandbox')

/**
 * The provider's own words for why checkout could not start.
 *
 * The API forwards `provider_detail` / `provider_code` from DCP on a refusal.
 * Collapsing those into one generic string is exactly what principle 7 exists
 * to prevent: "the app or catalog item is not allowed" tells an operator which
 * knob to turn, and "please try again" does not.
 */
function providerReason(error: ApiRequestError): string | null {
  if (!isApiRecord(error.errorData)) return null
  // FastAPI nests our structured payload under `detail`.
  const detail = error.errorData.detail
  if (!isApiRecord(detail)) return null
  // `??` alone keeps an empty string, which then fails the emptiness check and
  // discards the usable `message` sitting next to it.
  const specific = detail.provider_detail
  if (typeof specific === 'string' && specific !== '') return specific
  const fallback = detail.message
  return typeof fallback === 'string' && fallback !== '' ? fallback : null
}

type View = 'credits' | 'add' | 'usage'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="pb-1.5 text-sm font-semibold leading-5 text-[rgba(229,231,235,0.53)]">
      {children}
    </p>
  )
}

export default function UsageAndCreditsSection() {
  const { t, i18n } = useTranslation('settings')
  // owner_type / team_id on credit-pack and Pay clicks: the active team is the
  // one a purchase credits (RFC-1372 C8).
  const { activeTeam } = useActiveTeam()
  const activeTeamCtx = teamContextFor(activeTeam)
  // Every formatter below takes the ACTIVE UI locale. Defaulting to en-US
  // would render US grouping, currency placement and date order inside the
  // Korean and Vietnamese bundles.
  const locale = i18n.language
  const navigate = useNavigate()
  const { user } = useAuth()
  const { payment, clearPayment, close, entryPoint } = useSettingsModal()
  const userId = user?.id ?? 'anonymous'

  const [view, setView] = useState<View>('credits')
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null)
  const [orders, setOrders] = useState<SandboxOrder[]>(() => listOrders(userId))
  const [storageError, setStorageError] = useState(false)
  // Live mode creates a real DCP order; the placeholder path does not. This is
  // the mock/live axis only — the @meta.com audience gate is separate and still
  // decides whether this section exists at all.
  const liveCheckout = useBooleanFlagValue('billing_checkout_live_enabled', false)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)

  const balance = useSparks()
  const catalog = useBillingCatalog(view === 'add', liveCheckout ? 'live' : 'placeholder')

  // settings.usage_and_tokens.view (#3503) — top of the purchase funnel.
  //
  // Fires once per mount, not on every render: the section unmounts when the
  // settings dialog closes, so a mount IS a view. `entry_point` is the deep
  // link when one was used and the user menu otherwise — the two remaining
  // spec values (low_balance_prompt, dashboard_banner) have no surface yet, and
  // are deliberately not guessed at.
  const balanceCredits = balance.data?.balance
  const viewLoggedRef = useRef(false)
  useEffect(() => {
    if (viewLoggedRef.current) return
    viewLoggedRef.current = true
    trackUsageAndTokensView({
      entry_point: entryPoint,
      ...(typeof balanceCredits === 'number' ? { balance_credits: balanceCredits } : {}),
    })
  }, [balanceCredits, entryPoint])

  // Cross-tab: another tab placing an order must show up here.
  useEffect(() => {
    const refresh = () => setOrders(listOrders(userId))
    refresh()
    return subscribeToOrders(userId, refresh)
  }, [userId])

  // Memoised: `catalog.data ?? []` allocates a fresh array every render, which
  // would make the selection effect below re-run on each one.
  const packs = useMemo(() => catalog.data ?? [], [catalog.data])
  useEffect(() => {
    if (packs.length === 0) return
    // Re-pick when the current selection is not in this catalog, not only when
    // nothing is selected. The mode flag can flip while the modal is open, and
    // the two catalogs carry different item ids — a stale id leaves `selected`
    // null and Pay now disabled with no visible reason.
    if (selectedPackId !== null && packs.some((p) => p.id === selectedPackId)) return
    setSelectedPackId((packs.find((p) => p.recommended) ?? packs[0]).id)
  }, [packs, selectedPackId])

  // settings.token_catalog.view (#3503) — logged when a catalog actually
  // arrives, once per distinct catalog. Double-logged against the API's
  // token_catalog.fetch.success by design, so client-side loss between the two
  // is measurable; that API half does not exist yet.
  const catalogLoggedRef = useRef<string | null>(null)
  useEffect(() => {
    if (packs.length === 0) return
    const source = liveCheckout ? 'live' : 'placeholder'
    const key = `${source}:${packs.length}`
    if (catalogLoggedRef.current === key) return
    catalogLoggedRef.current = key
    trackTokenCatalogView({
      source,
      item_count: packs.length,
      currency: packs[0].price.currency,
    })
  }, [liveCheckout, packs])

  const successOrder = useMemo(() => {
    if (!payment) return null
    return orders.find((o) => o.orderId === payment.orderId && o.status === 'paid') ?? null
  }, [payment, orders])

  // The LIVE path asks the server to create a real DCP order. The placeholder
  // path below keeps working unchanged for local dev and for any environment
  // without the APNB credential — it is scaffolding, not a fallback, so a
  // failure in live mode is reported rather than silently downgraded.
  const startLiveCheckout = useCallback(
    async (pack: CreditPack) => {
      setStartError(null)
      setStarting(true)
      try {
        const handoff = await api.createCheckoutHandoff(pack.id)
        log({
          event: 'checkout:handoff',
          trace_id: `billing:${handoff.orderId}`,
          pack: pack.id,
          mode: handoff.paymentContainerMode,
        })
        close()
        // The handoff code rides in ROUTER STATE, not the URL and not storage.
        // It is a single-use bearer credential for Meta's checkout host; a
        // query param would put it in history, logs and the Referer chain.
        navigate(`/checkout?order=${encodeURIComponent(handoff.orderId)}`, {
          state: { handoff },
        })
      } catch (error) {
        // Stay put and say so. Navigating on a failed start would drop the user
        // on a checkout page with no order to pay for.
        log({
          event: 'checkout:handoff_failed',
          trace_id: 'billing:start',
          pack: pack.id,
        })
        // Principle 7: the backend forwards the provider's status, code and
        // detail, and a generic "please try again" throws away the only part
        // the user or an operator can act on.
        const specific = error instanceof ApiRequestError ? providerReason(error) : null
        setStartError(specific ?? t('settingsModal.billing.checkoutStartFailed'))
      } finally {
        setStarting(false)
      }
    },
    [close, navigate, t],
  )

  const startCheckout = useCallback(
    (pack: CreditPack) => {
      // Logged before the live/placeholder branch so both funnels are directly
      // comparable, and so an intent is recorded even if order creation then
      // fails.
      trackCheckoutInitiateClick({
        item_id: pack.id,
        source: liveCheckout ? 'live' : 'placeholder',
        credits: pack.credits,
        amount_minor: pack.price.amountMinor,
        currency: pack.price.currency,
        owner_type: activeTeamCtx.owner_type,
        team_id: activeTeamCtx.team_id,
      })
      if (liveCheckout) {
        void startLiveCheckout(pack)
        return
      }
      // Placeholder path: the order id is minted here and carried on the URL,
      // so /checkout can create-or-load idempotently and a refresh there does
      // not duplicate.
      const orderId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `ord_${Date.now()}_${Math.random().toString(36).slice(2)}`
      const order: SandboxOrder = {
        orderId,
        packId: pack.id,
        credits: pack.credits,
        price: pack.price,
        createdAt: new Date().toISOString(),
        status: 'pending',
        paidAt: null,
      }
      const written = saveOrder(userId, order)
      log({
        event: 'checkout:start',
        trace_id: `billing-sandbox:${orderId}`,
        pack: pack.id,
        stored: written.ok,
      })
      if (!written.ok) {
        // /checkout loads the order BY ID from this same store, so navigating
        // after a failed write would drop the user on the unknown-order state
        // with no way to place the pack they just chose. Keep them here and
        // say so.
        setStorageError(true)
        return
      }
      setStorageError(false)
      // Dismiss the dialog BEFORE leaving. The modal is provider state owned
      // above <Routes>, not route-scoped, so navigating does not unmount it —
      // without this it stays mounted and covers the checkout page.
      close()
      navigate(`/checkout?order=${encodeURIComponent(orderId)}`)
    },
    [close, liveCheckout, navigate, startLiveCheckout, userId, activeTeamCtx.owner_type, activeTeamCtx.team_id],
  )

  // ---- payment success -----------------------------------------------------
  if (payment) {
    return (
      <div className="flex flex-col gap-6" data-testid="billing-success">
        <SandboxNotice />
        <div className="flex flex-col items-center gap-4 pt-6 text-center">
          <CircleCheck className="h-14 w-14 text-success" strokeWidth={1.5} />
          <h4 className="text-2xl font-semibold text-white">
            {t('settingsModal.billing.successTitle')}
          </h4>
          {successOrder !== null ? (
            <p className="text-sm text-white/60">
              {t('settingsModal.billing.successBody', {
                credits: formatCredits(successOrder.credits, locale),
              })}
            </p>
          ) : (
            // payment=success with no resolvable order — a stale/foreign link.
            // Non-fatal: say so plainly rather than inventing an amount.
            <p className="text-sm text-white/60" data-testid="billing-success-unknown-order">
              {t('settingsModal.billing.successUnknownOrder')}
            </p>
          )}
        </div>

        {successOrder !== null && (
          <div className="overflow-hidden rounded-2xl bg-white/[0.06]">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-white/50">
                {t('settingsModal.billing.product')}
              </span>
              <span className="text-sm text-white">
                {t('settingsModal.billing.packLabel', {
                  credits: formatCredits(successOrder.credits, locale),
                })}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
              <span className="text-sm text-white/50">
                {t('settingsModal.billing.amountPaid')}
              </span>
              <span className="text-sm font-medium text-white">
                {formatMoney(successOrder.price, locale)}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          <a
            href={SAMPLE_RECEIPT_URL}
            download
            data-testid="billing-download-receipt"
            className="inline-flex items-center gap-2 text-sm text-white/70 underline-offset-4 hover:text-white hover:underline"
          >
            <Download className="h-4 w-4" />
            {t('settingsModal.billing.downloadReceipt')}
          </a>
          <button
            type="button"
            data-testid="billing-success-done"
            onClick={clearPayment}
            className="h-10 w-full max-w-[220px] rounded-full bg-white text-sm font-semibold text-black transition-colors hover:bg-white/90"
          >
            {t('settingsModal.billing.done')}
          </button>
        </div>
      </div>
    )
  }

  // ---- add credits ---------------------------------------------------------
  if (view === 'add') {
    const selected = packs.find((p) => p.id === selectedPackId) ?? null
    return (
      <div className="flex flex-col gap-4" data-testid="billing-add-credits">
        <button
          type="button"
          data-testid="billing-add-back"
          onClick={() => setView('credits')}
          className="inline-flex w-fit items-center gap-1 text-sm text-white/60 hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('settingsModal.billing.back')}
        </button>

        <SandboxNotice />

        {catalog.isLoading && (
          <p className="py-8 text-center text-sm text-white/50" data-testid="billing-catalog-loading">
            {t('settingsModal.billing.loadingCatalog')}
          </p>
        )}
        {catalog.isError && (
          <p className="py-8 text-center text-sm text-destructive" data-testid="billing-catalog-error">
            {t('settingsModal.billing.catalogFailed')}
          </p>
        )}

        {packs.length > 0 && (
          <div className="overflow-hidden rounded-2xl bg-white/[0.06]" role="radiogroup">
            {packs.map((pack, i) => {
              const isSelected = pack.id === selectedPackId
              return (
                <button
                  key={pack.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  data-testid={`billing-pack-${pack.id}`}
                  onClick={() => {
                    trackTokenCatalogOptionClick({
                      item_id: pack.id,
                      source: liveCheckout ? 'live' : 'placeholder',
                      credits: pack.credits,
                      amount_minor: pack.price.amountMinor,
                      currency: pack.price.currency,
                      discount_percent: pack.discountPercent,
                      recommended: pack.recommended,
                      owner_type: activeTeamCtx.owner_type,
                      team_id: activeTeamCtx.team_id,
                    })
                    setSelectedPackId(pack.id)
                  }}
                  className={cn(
                    'flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-white/[0.04]',
                    i > 0 && 'border-t border-white/[0.06]',
                  )}
                >
                  <span className="flex flex-col">
                    <span className="text-sm text-white">{formatMoney(pack.price, locale)}</span>
                    <span className="text-xs font-medium text-white/50">
                      {t('settingsModal.billing.packLabel', {
                        credits: formatCredits(pack.credits, locale),
                      })}
                      {pack.discountPercent > 0 && (
                        <span className="text-emerald-300">
                          {' · '}
                          {t('settingsModal.billing.percentOff', {
                            percent: pack.discountPercent,
                          })}
                        </span>
                      )}
                      {pack.recommended && ` · ${t('settingsModal.billing.recommended')}`}
                    </span>
                  </span>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-white" />}
                </button>
              )
            })}
          </div>
        )}

        {storageError && (
          <p className="text-sm text-destructive" data-testid="billing-storage-error">
            {t('settingsModal.billing.storageUnavailable')}
          </p>
        )}

        {startError !== null && (
          <p className="text-sm text-destructive" data-testid="billing-start-error">
            {startError}
          </p>
        )}

        <p className="text-xs font-medium leading-5 text-white/40">{t('settingsModal.billing.legal')}</p>

        <div className="flex justify-end">
          <button
            type="button"
            data-testid="billing-pay-now"
            disabled={selected === null || starting}
            onClick={() => selected && startCheckout(selected)}
            className="h-9 rounded-full bg-[#2f6bff] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#2a60e6] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {starting
              ? t('settingsModal.billing.startingCheckout')
              : t('settingsModal.billing.payNow')}
          </button>
        </div>
      </div>
    )
  }

  // ---- usage (real transaction history) ------------------------------------
  if (view === 'usage') {
    return (
      <div className="flex flex-col gap-4" data-testid="billing-usage">
        <button
          type="button"
          data-testid="billing-usage-back"
          onClick={() => setView('credits')}
          className="inline-flex w-fit items-center gap-1 text-sm text-white/60 hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('settingsModal.billing.back')}
        </button>
        <SectionLabel>{t('settingsModal.billing.usage')}</SectionLabel>
        <p className="text-sm text-white/50">{t('settingsModal.billing.usageComingSoon')}</p>
      </div>
    )
  }

  // ---- credits (default) ---------------------------------------------------
  return (
    <div className="flex flex-col gap-6" data-testid="billing-credits">
      <SandboxNotice />

      <div>
        <SectionLabel>{t('settingsModal.billing.credits')}</SectionLabel>
        <div className="overflow-hidden rounded-2xl bg-white/[0.12]">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex flex-col">
              <span className="text-sm text-white/60">
                {t('settingsModal.billing.availableCredits')}
              </span>
              {balance.isLoading ? (
                <span
                  className="mt-1 text-2xl text-white/40"
                  data-testid="billing-balance-loading"
                >
                  {t('settingsModal.billing.loadingBalance')}
                </span>
              ) : balance.isError ? (
                <span className="mt-1 text-sm text-destructive" data-testid="billing-balance-error">
                  {t('settingsModal.billing.balanceFailed')}
                </span>
              ) : (
                <span
                  className="text-hero font-light leading-tight text-white"
                  data-testid="billing-balance"
                >
                  {formatCredits(balance.data?.balance ?? 0, locale)}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 px-4 pb-4">
            <button
              type="button"
              data-testid="billing-add-credits-button"
              onClick={() => setView('add')}
              className="h-9 flex-1 rounded-full bg-white text-sm font-semibold text-black transition-colors hover:bg-white/90"
            >
              {t('settingsModal.billing.addCredits')}
            </button>
            <button
              type="button"
              data-testid="billing-view-usage-button"
              onClick={() => setView('usage')}
              className="h-9 flex-1 rounded-full bg-white/[0.12] text-sm font-medium text-white transition-colors hover:bg-white/20"
            >
              {t('settingsModal.billing.viewUsage')}
            </button>
          </div>

          {/* Auto-refill is presentational only — deliberately DISABLED rather
              than a live toggle that silently does nothing. There is no
              persistence API for it yet. */}
          <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
            <div className="flex flex-col">
              <span className="text-sm text-white">
                {t('settingsModal.billing.autoRefill')}
              </span>
              <span className="text-xs font-medium text-white/45">
                {t('settingsModal.billing.autoRefillUnavailable')}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={false}
              disabled
              data-testid="billing-auto-refill"
              aria-label={t('settingsModal.billing.autoRefill')}
              className="h-6 w-11 shrink-0 cursor-not-allowed rounded-full bg-white/15 opacity-50"
            >
              <span className="ml-0.5 block h-5 w-5 rounded-full bg-white/70" />
            </button>
          </div>
        </div>
      </div>

      <div>
        <SectionLabel>{t('settingsModal.billing.receipts')}</SectionLabel>
        {orders.filter((o) => o.status === 'paid').length === 0 ? (
          <p className="rounded-2xl bg-white/[0.04] px-4 py-6 text-center text-sm text-white/45"
             data-testid="billing-receipts-empty">
            {t('settingsModal.billing.noReceipts')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-2xl bg-white/[0.06]" data-testid="billing-receipts">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-2 text-xs font-medium text-white/40">
              <span>{t('settingsModal.billing.colDate')}</span>
              <span>{t('settingsModal.billing.colAmount')}</span>
              <span>{t('settingsModal.billing.colStatus')}</span>
              <span className="sr-only">{t('settingsModal.billing.downloadReceipt')}</span>
            </div>
            {orders
              .filter((o) => o.status === 'paid')
              .map((order) => (
                <div
                  key={order.orderId}
                  data-testid={`billing-receipt-${order.orderId}`}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 border-t border-white/[0.06] px-4 py-2.5 text-sm"
                >
                  <span className="text-white/80">
                    {new Date(order.paidAt ?? order.createdAt).toLocaleDateString(locale)}
                  </span>
                  <span className="text-white/80">{formatMoney(order.price, locale)}</span>
                  <span className="rounded-md bg-emerald-400/15 px-1.5 py-0.5 text-xs font-medium text-emerald-300">
                    {t('settingsModal.billing.statusTest')}
                  </span>
                  <a
                    href={SAMPLE_RECEIPT_URL}
                    download
                    aria-label={t('settingsModal.billing.downloadReceipt')}
                    className="text-white/50 hover:text-white"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
